import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { RobotOutlined, UserOutlined } from '@ant-design/icons';
import { Collapse, CollapseProps } from 'antd';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { CommentAddRequestBody, StepItemResponse } from '@/shared/api';
import { useRouterContext } from '@/shared/router';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { ChatMode, UserInstanceParams } from '@/shared/types';
import { Col, Row } from '@/shared/ui';
import { useInstanceAiConversation } from '@/widgets/chat/lib/useInstanceAiConversation';
import { useInstanceAgentConversation } from './lib/useInstanceAgentConversation';
import { useInstanceNotifications } from './lib/useInstanceNotifications';
import { ConversationMode } from './model/types';
import ChatConversationSwitch from './ui/ChatConversationSwitch';
import { ChatHistory } from './ui/ChatHistory';
import ChatInput from './ui/ChatInput';

type Props = {
  chatMode?: ChatMode;
  step: StepItemResponse;
  label?: string;
  displayNotificationDot?: boolean;
  disabled?: boolean;
  chatEnabled?: boolean;
  aiAssistantEnabled?: boolean;
  aiInputHidden?: boolean;
  postedBy: CommentAddRequestBody['postedBy'];
  accessToken?: string;
};

export type ChatRefProps = {
  openChat: () => void;
  closeChat: () => void;
};

export const InstanceStepChat: FC<Props> = ({
  label = 'Live chat',
  step,
  chatMode,
  displayNotificationDot = false,
  disabled = false,
  chatEnabled = true,
  aiAssistantEnabled = false,
  aiInputHidden = false,
  postedBy,
  accessToken,
}) => {
  const { procId: instanceId, code } = useParams<UserInstanceParams>();
  const { activeOrganization } = useRouterContext();
  const { theme } = useThemeContext();

  const [chatOpen, setChatOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenAIKeyDeleted, setIsOpenAIKeyDeleted] = useState(false);
  const [conversationMode, setConversationMode] = useState<ConversationMode>(
    aiAssistantEnabled ? ConversationMode.AI : ConversationMode.Agent,
  );

  const collapseRef = useRef<HTMLDivElement>(null);

  const queryKey =
    postedBy === 'workspaceMember'
      ? 'agentMessagesGetPaginated'
      : 'publicAgentMessagesGetPaginated';

  useEffect(() => {
    if (chatOpen) {
      setTimeout(() => {
        collapseRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 200);
    }
  }, [chatOpen, collapseRef]);

  const {
    agentConversationMessages,
    agentConversationLoading,
    hasAgentConversationNextPage,
    fetchNextAgentConversationPage,
    sendMessageToAgent,
    isMyMessageInAgentConversation,
  } = useInstanceAgentConversation({
    chatOpen,
    instanceId,
    stepId: step.id,
    queryKey,
    postedBy,
    chatMode,
    accessToken,
    code,
    setIsLoading,
  });

  const {
    threadId,
    aiConversationMessages,
    aiConversationLoading,
    waitingToStartGenerating,
    streamedMessage,
    sendMessageToAi,
    isMyMessageInAiConversation,
  } = useInstanceAiConversation({
    chatMode,
    chatOpen,
    stepId: step.id,
    stepThreadId: step.aiThreadId,
    setIsOpenAIKeyDeleted,
    setIsLoading,
  });

  const { markStepNotificationsAsSeen } = useInstanceNotifications({
    activeOrgId: activeOrganization?.id,
    instanceId: instanceId!,
    stepId: step.id,
    postedBy,
    chatMode,
    code,
    collapseRef,
    setChatOpen,
    setConversationMode,
  });

  const handleChatOpen = (key: string | string[]) => {
    const open = Boolean(key.length);
    setChatOpen(open);
    if (
      open &&
      conversationMode === ConversationMode.Agent &&
      displayNotificationDot
    ) {
      markStepNotificationsAsSeen();
    }
  };

  const conversationOptions = useMemo(() => {
    const options = [];
    if (aiAssistantEnabled) {
      options.push({
        label: <span data-testid="ai-label">AI</span>,
        value: ConversationMode.AI,
        icon: <RobotOutlined />,
      });
    }
    if (chatEnabled) {
      options.push({
        label: displayNotificationDot ? (
          <span data-testid="agent-label">
            Agent <SegmentedDot />
          </span>
        ) : (
          <span data-testid="agent-label">Agent</span>
        ),
        value: ConversationMode.Agent,
        icon: <UserOutlined />,
      });
    }
    return options;
  }, [chatEnabled, aiAssistantEnabled, displayNotificationDot]);

  const onChatConversationChange = (value: ConversationMode) => {
    setConversationMode(value);
    if (value === ConversationMode.Agent && displayNotificationDot) {
      markStepNotificationsAsSeen();
    }
  };

  const isInputHidden =
    conversationMode === ConversationMode.AI && aiInputHidden;

  const isInputDisabled = useMemo(
    () =>
      isLoading ||
      chatMode === ChatMode.Preview ||
      (chatMode === ChatMode.User && isOpenAIKeyDeleted),
    [isLoading, chatMode, isOpenAIKeyDeleted],
  );

  const inputPlaceholder = useMemo(
    () =>
      conversationMode === ConversationMode.Agent
        ? chatMode !== ChatMode.Author
          ? 'Message agent'
          : 'Message user'
        : 'Ask AI',
    [conversationMode, chatMode],
  );

  const sendMessageCallback = useMemo(
    () =>
      conversationMode === ConversationMode.Agent
        ? sendMessageToAgent
        : sendMessageToAi,
    [conversationMode, sendMessageToAgent, sendMessageToAi],
  );

  const collapseItems: CollapseProps['items'] = [
    {
      key: `chat-${step.id}`,
      label: (
        <div>
          <span>{label}</span>
          {displayNotificationDot && <NotificationDot />}
        </div>
      ),
      children: (
        <div>
          <ContentWrapper>
            {conversationMode === ConversationMode.Agent && chatEnabled && (
              <ChatHistory
                messages={agentConversationMessages}
                chatLoading={agentConversationLoading}
                hasNextPage={hasAgentConversationNextPage}
                fetchNextPage={fetchNextAgentConversationPage}
                isMyMessage={isMyMessageInAgentConversation}
                chatMode={chatMode}
              />
            )}
            {conversationMode === ConversationMode.AI && aiAssistantEnabled && (
              <ChatHistory
                messages={aiConversationMessages}
                chatLoading={aiConversationLoading && Boolean(threadId)}
                messageLoading={waitingToStartGenerating}
                isMyMessage={isMyMessageInAiConversation}
                chatMode={chatMode}
                streamedMessage={streamedMessage}
              />
            )}
          </ContentWrapper>
          <MessageInputWrapper
            style={{
              borderTop: `1px solid ${themeConfig[theme].colors.border}`,
            }}
          >
            {disabled ? (
              <em style={{ opacity: 0.7 }}>Comments are disabled.</em>
            ) : (
              <Col alignItems="flex-start" style={{ width: '100%' }}>
                <Row
                  alignItems="center"
                  style={{ marginBottom: 10, position: 'relative' }}
                >
                  <ChatConversationSwitch
                    chatMode={chatMode}
                    conversationOptions={conversationOptions}
                    conversationMode={conversationMode}
                    onChatConversationChange={onChatConversationChange}
                  />
                </Row>
                <Row gap={5} direction="column">
                  {!isInputHidden && (
                    <ChatInput
                      canUpload={conversationMode === ConversationMode.Agent}
                      disabled={isInputDisabled}
                      placeholder={inputPlaceholder}
                      isLoading={isLoading}
                      sendMessage={sendMessageCallback}
                    />
                  )}
                </Row>
              </Col>
            )}
          </MessageInputWrapper>
          {conversationMode === ConversationMode.AI && !aiInputHidden && (
            <Info>
              AI Assistant can make mistakes. Consider checking important
              information.
            </Info>
          )}
        </div>
      ),
    },
  ];

  return (
    <Collapse
      data-testid="chat-collapse"
      ref={collapseRef}
      size="small"
      onChange={handleChatOpen}
      style={{ width: '100%' }}
      items={collapseItems}
    />
  );
};

const ContentWrapper = styled.div`
  height: 50vh;
`;

const MessageInputWrapper = styled.div`
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  border-top: 1px solid #d9d9d9;
  position: relative;
  display: flex;
  padding: 16px 12px;
  margin-bottom: 10px;

  .ant-input-data-count {
    font-size: 11px;
  }
`;

const NotificationDot = styled.div`
  background: #ff4d4f;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  position: absolute;
  right: 10px;
  top: 10px;
`;

const Info = styled.div`
  opacity: 0.6;
  font-size: 11px;
  text-align: center;
  margin-top: -5px;
  margin-bottom: 5px;
`;

const SegmentedDot = styled.div`
  background: #ff4d4f;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  position: absolute;
  right: 3px;
  top: 2px;
  border: 1px solid #fff;
`;
